/**
 * The ruler above the page.
 *
 * IT IS A MEASURE, AND THAT IS THE POINT. A procedure is written to be printed: the author needs to
 * know how wide the text column actually is, where the margins fall, and whether a table they are
 * about to insert will fit across it. On a 27-inch screen a text column tells you none of that by
 * itself -- it just looks comfortable, right up until the printer disagrees.
 *
 * DRAWN IN REAL INCHES, from the same numbers DocumentPaper uses, so the marks land exactly on the
 * page's own edges rather than near them.
 *
 * THE MARGINS ARE NOT SHADED. They were, as grey ends -- which is what Google does and what this
 * copied. Ed's call, and it is the better one here: the shading was the loudest thing on a strip
 * whose job is to be read, and once the strip carries MARKERS the grey competes with them for the
 * one thing the eye should land on. The measure is still legible without it, because the numbering
 * starts at the margin and the markers sit on it.
 *
 * MARGINS ARE STILL NOT DRAGGABLE, and that part of the old reasoning stands: a controlled
 * document's page setup belongs to its template, and a marker that silently moved it would make
 * two revisions of one procedure print differently with nothing in the record saying why. What the
 * markers move is a PARAGRAPH -- its left edge, its right edge, and its first line -- which is a
 * property of the text and belongs to whoever is writing it. See RulerMarkers.
 */
import React from "react";
import { Box } from "@mui/material";

import { Grey5, Grey17 } from "../../theme/styles";
import RulerMarkers from "./RulerMarkers";
import { pageGeometry, pageSetup } from "./PageElements";

/**
 * The sheet this ruler is numbering, from the document itself.
 *
 * IT USED TO SAY "Matches DocumentPaper exactly. If one changes the other must" over four
 * constants -- and by the time orientation and margins were built it already did not: the top
 * margin here was 0.9in against the paper's 1in, so every number on the vertical ruler was a tenth
 * of an inch out from the page beside it. A comment is not a mechanism. Both now read
 * pageGeometry, which is also what the pagination preview and the renderer measure against.
 */
function sheetOf(editor: any) {
    return pageGeometry(pageSetup(editor));
}

/** The ruler's own thickness, shared so the horizontal one can leave room for the vertical. */
export const RULER_THICKNESS = 22;

/** Kept for the vertical ruler's own inset from the canvas edge. */
export const RULER_GAP = 0;

export default function EditorRuler({ fullWidth, editor, editable, editorState }: {
    fullWidth?: boolean;
    /** The editor, so the markers can read the caret's paragraph and write back to it. */
    editor?: any;
    editable?: boolean;
    /** Bumped on every transaction, so the markers follow the caret. */
    editorState?: number;
}) {
    // A FULL-WIDTH PAGE HAS NO FIXED MEASURE, so there is nothing honest to number. Drawing a
    // ruler whose inches are not inches would be worse than drawing none.
    if (fullWidth) return null;

    const sheet = sheetOf(editor);
    const pageInches = sheet.widthIn;
    const leftInches = sheet.marginLeft;
    const rightInches = sheet.marginRight;

    const ticks: React.ReactNode[] = [];
    // Every 1/8 inch: short marks, taller at the half, numbered at the whole -- the subdivision
    // every ruler on a desk uses, so it reads without being studied.
    for (let eighth = 0; eighth <= pageInches * 8; eighth += 1) {
        const inches = eighth / 8;
        const whole = eighth % 8 === 0;
        const half = eighth % 4 === 0;
        const inMargin = inches < leftInches || inches > pageInches - rightInches;
        ticks.push(
            <Box key={eighth} sx={{
                position: "absolute", left: `${inches}in`, bottom: 0,
                width: "1px", height: whole ? 9 : half ? 6 : 3,
                backgroundColor: inMargin ? "#BDC1C6" : Grey5,
            }}/>);
        // THE NUMBERS COUNT FROM THE MARGIN, NOT FROM THE PAPER'S EDGE, which is what every word
        // processor does and what an author means by "indent it two inches": two inches into the
        // text, not two inches from a sheet edge they never see.
        if (whole && !inMargin && inches < pageInches - rightInches) {
            const measure = inches - leftInches;
            ticks.push(
                <Box key={`n${eighth}`} sx={{
                    position: "absolute", left: `${inches}in`, bottom: 10,
                    transform: "translateX(-50%)", fontSize: 9, color: Grey17,
                    fontFamily: "Roboto, Arial, sans-serif", lineHeight: 1,
                }}>{measure === 0 ? "" : measure}</Box>);
        }
    }

    return (
        <Box sx={{ display: "flex", justifyContent: "center", pt: 1 }}>
            {/* NO SPACER. The vertical ruler is pinned to the left edge of the canvas and takes
                no width in the flow, so the page is centred in the whole canvas and this ruler --
                centred on the same axis -- lands exactly above it. It DID need a spacer, back when
                the two sat in one row and the ruler pushed the page right. */}
            <Box aria-hidden={editor ? undefined : "true"} sx={{
                position: "relative", width: `${pageInches}in`, height: RULER_THICKNESS, flexShrink: 0,
                backgroundColor: "#FFFFFF",
                borderRadius: "2px",
                boxShadow: "inset 0 0 0 1px rgba(60,64,67,.12)",
            }}>
                {ticks}
                {/* WHERE THE TEXT COLUMN BEGINS AND ENDS, now that no shading says so. One hairline
                    at each margin: enough to read the measure off, quiet enough not to compete with
                    the markers sitting on it. */}
                {[leftInches, pageInches - rightInches].map((inches) => (
                    <Box key={`m${inches}`} sx={{
                        position: "absolute", left: `${inches}in`, top: 3, bottom: 3,
                        width: "1px", backgroundColor: "#C9CDD2",
                    }}/>
                ))}
                {editor && (
                    <RulerMarkers editor={editor} editable={editable}
                                  marginInches={leftInches}
                                  measureInches={pageInches - leftInches - rightInches}
                                  editorState={editorState}/>
                )}
            </Box>
        </Box>
    );
}


/**
 * The ruler down the left of the page.
 *
 * IT MEASURES THE OTHER AXIS, which for a controlled document is the one that decides how much
 * fits on a page -- and therefore where the page breaks fall, which is what a reviewer asks about
 * when they say "this should start on a new page". Its grey ends are the top and bottom margins,
 * drawn from the same numbers DocumentPaper uses.
 *
 * MINIMUM HEIGHT OF ONE PAGE, GROWING WITH THE DOCUMENT. A ruler that stopped at 11 inches on a
 * four-page procedure would say the document was one page long. It is stretched by the paper beside
 * it, so the two cannot disagree.
 *
 * Not draggable, for the reason the horizontal one is not: margins belong to the rendition, and the
 * rendition belongs to the template.
 */
export function EditorRulerVertical({ geometry }: {
    /**
     * The sheet being numbered. PASSED IN rather than read here, because this one is drawn by
     * DocumentPaper and has no editor to ask -- and a vertical ruler that numbered a different
     * sheet from the page it is pinned to is worse than no ruler.
     */
    geometry?: ReturnType<typeof pageGeometry>;
} = {}) {
    const sheet = geometry ?? pageGeometry(null);
    const heightInches = sheet.heightIn;
    const topInches = sheet.marginTop;
    const bottomInches = sheet.marginBottom;

    const ticks: React.ReactNode[] = [];
    for (let eighth = 0; eighth <= heightInches * 8; eighth += 1) {
        const inches = eighth / 8;
        const whole = eighth % 8 === 0;
        const half = eighth % 4 === 0;
        const inMargin = inches < topInches || inches > heightInches - bottomInches;
        ticks.push(
            <Box key={eighth} sx={{
                position: "absolute", top: `${inches}in`, right: 0,
                height: "1px", width: whole ? 9 : half ? 6 : 3,
                backgroundColor: inMargin ? "#BDC1C6" : Grey5,
            }}/>);
        if (whole && !inMargin && inches < heightInches - bottomInches) {
            const measure = Math.round((inches - topInches) * 10) / 10;
            ticks.push(
                <Box key={`n${eighth}`} sx={{
                    position: "absolute", top: `${inches}in`, right: 11,
                    transform: "translateY(-50%)", fontSize: 9, color: Grey17,
                    fontFamily: "Roboto, Arial, sans-serif", lineHeight: 1,
                }}>{measure <= 0 ? "" : Math.round(measure)}</Box>);
        }
    }

    return (
        <Box aria-hidden="true" sx={{
            // PINNED TO THE CANVAS'S LEFT EDGE, which is where a word processor puts it: it is an
            // instrument belonging to the workspace, not a margin belonging to the paper. Beside
            // the page it read as part of the sheet; out here it reads as a ruler on a desk.
            //
            // ABSOLUTE, so it takes no width in the flow -- the page stays centred in the WHOLE
            // canvas rather than in whatever is left over after the ruler.
            position: "absolute", left: 0, top: 0, bottom: 0,
            width: `${RULER_THICKNESS}px`,
            // UNSHADED, like the horizontal one: the margins are marked, not filled in.
            backgroundColor: "#FFFFFF",
            borderRadius: "2px",
            boxShadow: "inset 0 0 0 1px rgba(60,64,67,.12)",
        }}>
            {ticks}
            {/* The top and bottom margins, as hairlines rather than fill. */}
            {[topInches].map((inches) => (
                <Box key={`vm${inches}`} sx={{
                    position: "absolute", top: `${inches}in`, left: 3, right: 3,
                    height: "1px", backgroundColor: "#C9CDD2",
                }}/>
            ))}
            <Box sx={{
                position: "absolute", bottom: `${bottomInches}in`, left: 3, right: 3,
                height: "1px", backgroundColor: "#C9CDD2",
            }}/>
        </Box>
    );
}
